import { TKey } from "@/app/translations/TKey";
import {
  ProspectCriteriaField,
  ProspectSearchCriteria,
} from "@/app/types/prospect/ProspectSearchCriteria";

export enum ProspectDisplayMode {
  BADGE = "badge",
  LIST = "list",
  LINK = "link",
}

export function splitAvatarUpdatedMessage(text: string): [string, string] {
  const [before, after = ""] = text.split("{{ago}}");
  return [before, after];
}

// Column density of a group's category grid, matched to its content:
// DENSE for short badge chips (up to 3 cols), COMFORT for longer text lists
// (up to 2 cols), FULL for a single full-width card (recommended platforms).
export enum ProspectStrategyLayout {
  DENSE = "dense",
  COMFORT = "comfort",
  FULL = "full",
}

export type ProspectStrategyCategory = {
  field: ProspectCriteriaField;
  labelKey: TKey;
  display: ProspectDisplayMode;
  color?: string;
  placeholderWidths: string[];
};

export type ProspectStrategyGroup = {
  titleKey: TKey;
  layout: ProspectStrategyLayout;
  categories: ProspectStrategyCategory[];
};

export function categoriesLayoutClass(layout: ProspectStrategyLayout): string {
  switch (layout) {
    case ProspectStrategyLayout.DENSE:
      return "categoriesDense";
    case ProspectStrategyLayout.COMFORT:
      return "categoriesComfort";
    case ProspectStrategyLayout.FULL:
      return "categoriesFull";
  }
}

export const PROSPECT_STRATEGY_GROUPS: ProspectStrategyGroup[] = [
  {
    titleKey: TKey.PROSPECT_SEARCH_GROUP_CRITERIA,
    layout: ProspectStrategyLayout.DENSE,
    categories: [
      {
        field: ProspectCriteriaField.TARGET_ROLES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_ROLES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["78px", "64px", "92px"],
      },
      {
        field: ProspectCriteriaField.TARGET_SENIORITIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_SENIORITIES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["64px", "80px", "56px"],
      },
      {
        field: ProspectCriteriaField.TARGET_FUNCTIONS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_FUNCTIONS,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["72px", "60px", "84px"],
      },
      {
        field: ProspectCriteriaField.TARGET_INDUSTRIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_INDUSTRIES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["88px", "72px"],
      },
      {
        field: ProspectCriteriaField.TARGET_BUSINESS_MODELS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_BUSINESS_MODELS,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["70px", "96px", "58px"],
      },
      {
        field: ProspectCriteriaField.TARGET_COMPANY_STAGES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_COMPANY_STAGES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["64px", "84px", "72px", "56px"],
      },
      {
        field: ProspectCriteriaField.TARGET_COMPANY_SIZES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_COMPANY_SIZES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["72px", "96px"],
      },
      {
        field: ProspectCriteriaField.TARGET_TECHNOLOGIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_TECHNOLOGIES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["84px", "68px", "92px"],
      },
      {
        field: ProspectCriteriaField.TARGET_COMPANIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_COMPANIES,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["76px", "60px", "88px"],
      },
      {
        field: ProspectCriteriaField.TARGET_LOCATIONS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TARGET_LOCATIONS,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["60px", "52px", "68px", "80px"],
      },
      {
        field: ProspectCriteriaField.SEARCH_KEYWORDS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_SEARCH_KEYWORDS,
        display: ProspectDisplayMode.BADGE,
        placeholderWidths: ["90px", "64px", "108px", "72px"],
      },
      {
        field: ProspectCriteriaField.SEARCH_QUERIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_SEARCH_QUERIES,
        display: ProspectDisplayMode.LIST,
        placeholderWidths: ["140px", "112px"],
      },
    ],
  },
  {
    titleKey: TKey.PROSPECT_SEARCH_GROUP_PLATFORMS,
    layout: ProspectStrategyLayout.FULL,
    categories: [
      {
        field: ProspectCriteriaField.RECOMMENDED_PLATFORMS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_RECOMMENDED_PLATFORMS,
        display: ProspectDisplayMode.LINK,
        placeholderWidths: ["80px", "64px", "96px"],
      },
    ],
  },
  {
    titleKey: TKey.PROSPECT_SEARCH_GROUP_SIGNALS,
    layout: ProspectStrategyLayout.COMFORT,
    categories: [
      {
        field: ProspectCriteriaField.MUST_HAVE_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_MUST_HAVE_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-signal)",
        placeholderWidths: ["120px", "100px", "132px"],
      },
      {
        field: ProspectCriteriaField.PREFERRED_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_PREFERRED_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-feature)",
        placeholderWidths: ["112px", "128px", "96px"],
      },
      {
        field: ProspectCriteriaField.PAIN_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_PAIN_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-pain)",
        placeholderWidths: ["116px", "96px", "124px"],
      },
      {
        field: ProspectCriteriaField.TRIGGER_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TRIGGER_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-objection)",
        placeholderWidths: ["108px", "128px"],
      },
      {
        field: ProspectCriteriaField.TRUST_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_TRUST_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-verbatim)",
        placeholderWidths: ["104px", "120px", "88px"],
      },
    ],
  },
  {
    titleKey: TKey.PROSPECT_SEARCH_GROUP_EXCLUSIONS,
    layout: ProspectStrategyLayout.COMFORT,
    categories: [
      {
        field: ProspectCriteriaField.EXCLUDE_SIGNALS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_EXCLUDE_SIGNALS,
        display: ProspectDisplayMode.LIST,
        color: "var(--color-analysis-pain)",
        placeholderWidths: ["100px", "84px"],
      },
      {
        field: ProspectCriteriaField.EXCLUDE_KEYWORDS,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_EXCLUDE_KEYWORDS,
        display: ProspectDisplayMode.BADGE,
        color: "var(--color-analysis-pain)",
        placeholderWidths: ["72px", "56px", "88px"],
      },
      {
        field: ProspectCriteriaField.EXCLUDE_COMPANIES,
        labelKey: TKey.PROSPECT_SEARCH_CRITERIA_EXCLUDE_COMPANIES,
        display: ProspectDisplayMode.BADGE,
        color: "var(--color-analysis-pain)",
        placeholderWidths: ["80px", "64px"],
      },
    ],
  },
];

export function hasCriteriaData(criteria: ProspectSearchCriteria | null | undefined): boolean {
  if (!criteria) {
    return false;
  }
  return PROSPECT_STRATEGY_GROUPS.some((group) =>
    group.categories.some((category) => (criteria[category.field]?.length ?? 0) > 0),
  );
}

export function getLinkDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function openLinkFromRow(target: HTMLElement, url: string): void {
  if (target.closest("button") || target.closest("input") || target.closest("textarea")) {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
